import { ChangeEvent, FunctionComponent, useState, useMemo } from "react";
import {
    FormControl,
    FormHelperText,
    Grid,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    Switch,
    FormControlLabel
} from "@material-ui/core";
import { PaymentBonusValue, FreeSpinsData } from "../../../interfaces";
import { games } from "../games-data";

const COMMON_DENOMINATIONS = [0.5, 1, 2, 5, 10];

type FieldPaymentBonusProps = {
    error: boolean;
    setValue: (val: PaymentBonusValue) => void;
    value: PaymentBonusValue;
    formHelperText: string;
};

const FieldPaymentBonus: FunctionComponent<FieldPaymentBonusProps> = ({
    error,
    setValue,
    value,
    formHelperText,
}) => {
    const isComplex = typeof value === "object";
    const amount = isComplex ? value.amount : (value || 0);
    const fs = isComplex && value.free_spins ? value.free_spins : null;

    const [enableFs, setEnableFs] = useState<boolean>(!!fs && fs.game_id !== undefined);

    const selectedGame = useMemo(() => games.find(g => g.id === fs?.game_id), [fs?.game_id]);
    const provider = selectedGame?.provider || "inout";

    const { allowedDenominations, allowedBetLevels, showBetLevel } = useMemo(() => {
        switch (provider) {
            case "pragmatic":
                return {
                    allowedDenominations: COMMON_DENOMINATIONS,
                    allowedBetLevels: [1],
                    showBetLevel: true
                };
            case "netent":
                return {
                    allowedDenominations: COMMON_DENOMINATIONS,
                    allowedBetLevels: [1],
                    showBetLevel: true
                };
            case "inout":
            default:
                return {
                    allowedDenominations: COMMON_DENOMINATIONS,
                    allowedBetLevels: [],
                    showBetLevel: false
                };
        }
    }, [provider]);

    const updateData = (newAmount: number, newFs: FreeSpinsData | null, enabled: boolean) => {
        if (!enabled || !newFs || newFs.game_id === undefined) {
            setValue(newAmount);
        } else {
            setValue({ amount: newAmount, free_spins: newFs });
        }
    };

    const onAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.trim();
        if (val === "" || val.match(/^\d+$/)) {
            const num = val === "" ? 0 : Number(val);
            updateData(num, fs, enableFs);
        }
    };

    const onFsChange = (field: keyof FreeSpinsData, val: any) => {
        const newFs = { ...(fs || {}), [field]: val };
        updateData(amount, newFs, true);
    };

    const onGameChange = (gameId: number) => {
        if (!gameId) {
            updateData(amount, null, enableFs);
            return;
        }

        const newSelectedGame = games.find(g => g.id === gameId);
        const nextProvider = newSelectedGame?.provider || "inout";

        const nextFs: FreeSpinsData = {
            ...(fs || {}),
            game_id: gameId,
            bet_level: nextProvider === "pragmatic" ? 1 : undefined
        };

        if (fs?.line_bet && !COMMON_DENOMINATIONS.includes(fs.line_bet)) {
            nextFs.line_bet = undefined;
        }

        updateData(amount, nextFs, true);
    };

    const toggleFs = (e: ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setEnableFs(checked);
        updateData(amount, fs, checked);
    };

    return (
        <Grid item xs={12}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <FormControl error={error} fullWidth size="small">
                        <TextField
                            type="number"
                            name="payment_bonus"
                            value={amount}
                            onChange={onAmountChange}
                            size="small"
                            label="Сумма бонуса (%)"
                        />
                        <FormHelperText>{formHelperText}</FormHelperText>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <FormControlLabel
                        control={<Switch checked={enableFs} onChange={toggleFs} color="primary" size="small" />}
                        label="Добавить фриспины"
                    />
                </Grid>

                {enableFs && (
                    <>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Игра</InputLabel>
                                <Select
                                    value={fs?.game_id || ""}
                                    onChange={(e) => onGameChange(Number(e.target.value))}
                                >
                                    <MenuItem value=""><em>Не выбрано</em></MenuItem>
                                    {games.map(g => (
                                        <MenuItem key={g.id} value={g.id}>{g.title}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Количество"
                                value={fs?.count ?? ""}
                                onChange={(e) => onFsChange("count", e.target.value ? Number(e.target.value) : 1)}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" disabled={!fs?.game_id}>
                                <InputLabel>Ставка на линию (Denomination)</InputLabel>
                                <Select
                                    value={fs?.line_bet ?? ""}
                                    onChange={(e) => onFsChange("line_bet", Number(e.target.value))}
                                >
                                    {allowedDenominations.map(val => (
                                        <MenuItem key={val} value={val}>{val}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {showBetLevel && (
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small" disabled={!fs?.game_id}>
                                    <InputLabel>Уровень ставки (Freeround Bet)</InputLabel>
                                    <Select
                                        value={fs?.bet_level ?? ""}
                                        onChange={(e) => onFsChange("bet_level", e.target.value ? Number(e.target.value) : "")}
                                    >
                                        {allowedBetLevels.map(val => (
                                            <MenuItem key={val} value={val}>{val}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}

                        <Grid item xs={12} sm={12}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Вейджер с фриспинов (%)"
                                value={fs?.wager ?? ""}
                                onChange={(e) => onFsChange("wager", e.target.value ? Number(e.target.value) : "")}
                                inputProps={{ step: "1", min: "0" }}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        </Grid>
    );
};

export default FieldPaymentBonus;